import { useReadQuery } from "@apollo/client";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { DefaultizedPieValueType } from "@mui/x-charts";
import DonutChart from "components/Dashboard/DonutChart";
import { TruncateTypography } from "components/TruncateTypography";
import { RegulatoryStatusOption } from "generated-graphql/graphql";
import pluralize from "pluralize";
import { useMemo } from "react";

import { ALL_GENERATOR_STATUS_OPTIONS } from "../../types";
import { useWasteOverviewState } from "../../WasteOverviewContext";
import {
  getEmptyDonutColor,
  getInactiveStatusColors,
  statusAbbreviatedLabels,
  getStatusColors,
} from "./constants";

export function FacilitiesByGeneratorStatusChart() {
  const theme = useTheme();
  const statusColors = getStatusColors(theme);
  const {
    overviewState: { filters },
    setOverviewState,
    facilitiesByLocationAndGeneratorStatusQuery,
  } = useWasteOverviewState();

  const { data } = useReadQuery(facilitiesByLocationAndGeneratorStatusQuery);

  const isMd = useMediaQuery(theme.breakpoints.down("md"));

  // Group data by generator status and sum counts for donut chart
  const groupedData = useMemo(() => {
    if (data.wasteFacilitiesByGeneratorStatusAndState.length === 0) {
      return null;
    }

    const rawGrouped = data.wasteFacilitiesByGeneratorStatusAndState.reduce(
      (acc, item) => {
        const status = item.generatorStatus;
        if (status) {
          if (!acc[status]) {
            acc[status] = 0;
          }
          acc[status] += item.count;
        } else {
          if (!acc[RegulatoryStatusOption.None]) {
            acc[RegulatoryStatusOption.None] = 0;
          }
          acc[RegulatoryStatusOption.None] += item.count;
        }
        return acc;
      },
      {} as Record<RegulatoryStatusOption, number>
    );

    // Always order the data by the order of ALL_GENERATOR_STATUS_OPTIONS
    const orderedGrouped: Record<RegulatoryStatusOption, number> = {} as Record<
      RegulatoryStatusOption,
      number
    >;
    ALL_GENERATOR_STATUS_OPTIONS.forEach((status) => {
      orderedGrouped[status as RegulatoryStatusOption] =
        rawGrouped[status as RegulatoryStatusOption] || 0;
    });

    return orderedGrouped;
  }, [data.wasteFacilitiesByGeneratorStatusAndState]);

  // Transform data for the donut chart
  const seriesData = useMemo(() => {
    if (groupedData === null) {
      return [
        {
          id: "no-facilities",
          value: 1,
          color: getEmptyDonutColor(theme),
        },
      ];
    }
    const inactiveStatusColors = getInactiveStatusColors(theme);
    return Object.entries(groupedData).map(([status, count]) => {
      const isSelected = filters.currentGeneratorStatuses?.includes(
        status as RegulatoryStatusOption
      );
      return {
        id: status,
        value: count,
        color: isSelected
          ? statusColors[status as keyof typeof statusColors]
          : inactiveStatusColors[status as keyof typeof inactiveStatusColors],
      };
    });
  }, [groupedData, filters.currentGeneratorStatuses, statusColors, theme]);

  const legendItems = useMemo(() => {
    return seriesData.map(({ value, id }) => {
      const isSelected = filters.currentGeneratorStatuses?.includes(id as RegulatoryStatusOption);

      const statusLabel = statusAbbreviatedLabels[id as keyof typeof statusAbbreviatedLabels];
      return {
        color: statusColors[id as keyof typeof statusColors],
        isSelected,
        tooltip: `${statusLabel} - ${value} ${pluralize("facility", value)}`,
        value: (
          <Box
            display="flex"
            width="100%"
            justifyContent="space-between"
            sx={{ opacity: isSelected ? 1 : 0.3 }}
            gap={2}
          >
            <Typography variant="body2">{statusLabel}</Typography>
            <TruncateTypography variant="body2">
              {value} {pluralize("facility", value)}
            </TruncateTypography>
          </Box>
        ),
      };
    });
  }, [seriesData, filters.currentGeneratorStatuses, statusColors]);

  const totalSelectedFacilities = useMemo(() => {
    if (groupedData === null) {
      return 0;
    }

    if (!filters.currentGeneratorStatuses || filters.currentGeneratorStatuses.length === 0) {
      // If no filter, show total of all facilities
      return Object.values(groupedData).reduce((sum, count) => sum + count, 0);
    }

    // Sum only the selected statuses
    return filters.currentGeneratorStatuses.reduce((sum, status) => {
      return sum + (groupedData[status] || 0);
    }, 0);
  }, [filters.currentGeneratorStatuses, groupedData]);

  const onClickGeneratorStatus = (item: DefaultizedPieValueType) => {
    if (groupedData === null) {
      return;
    }

    setOverviewState((state) => {
      const clickedStatus = item.id as RegulatoryStatusOption;
      const currentFilter = state.currentGeneratorStatuses || [];

      const isCurrentlyFilteredToThis =
        currentFilter.length === 1 && currentFilter[0] === clickedStatus;

      if (isCurrentlyFilteredToThis) {
        // Reset to all statuses if clicking the currently filtered status.
        // This matches the behavior of the other dashboards.
        state.currentGeneratorStatuses = [...ALL_GENERATOR_STATUS_OPTIONS];
      } else {
        state.currentGeneratorStatuses = [clickedStatus];
      }
    });
  };

  const facilityCountLabel = useMemo(() => {
    if (groupedData === null) {
      return undefined;
    }

    return filters.currentGeneratorStatuses?.length === ALL_GENERATOR_STATUS_OPTIONS.length
      ? pluralize("Total Facility", totalSelectedFacilities)
      : pluralize("Facility", totalSelectedFacilities);
  }, [filters.currentGeneratorStatuses, groupedData, totalSelectedFacilities]);

  return (
    <DonutChart
      seriesData={seriesData}
      label={groupedData === null ? undefined : totalSelectedFacilities.toLocaleString()}
      subLabel={facilityCountLabel}
      containerHeight="18rem"
      legend={{
        direction: "column",
        position: "right",
      }}
      legendSx={{
        minWidth: 140,
        maxWidth: 200,
        width: "100%",
      }}
      onItemClick={(event, pieItemIdentifier, item) => onClickGeneratorStatus(item)}
      onLegendItemClick={(item: DefaultizedPieValueType) => onClickGeneratorStatus(item)}
      legendItems={
        groupedData === null
          ? [
              {
                color: "transparent",
                value: "🏭 No facilities to show",
              },
            ]
          : legendItems
      }
      justifyContent={isMd ? "center" : "start"}
    />
  );
}
